import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Search, Loader2, X } from 'lucide-react';
import DeliverySearchModal from './DeliverySearchModal';

function DeliverySearchButton({ userId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  const baseUrl = process.env.REACT_APP_API_URL || '';

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Set new timeout
    debounceRef.current = setTimeout(() => {
      performSearch();
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery]);

  async function performSearch() {
    if (searchQuery.length < 2) return;

    setLoading(true);
    try {
      const response = await axios.get(`${baseUrl}/api/delivery_points/search`, {
        params: {
          admin_user_id: userId,
          query: searchQuery
        }
      });

      setSearchResults(response.data.results || []);
    } catch (error) {
      console.error('Error searching deliveries:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleSelectDelivery(deliveryId) {
    try {
      const response = await axios.get(
        `${baseUrl}/api/delivery_points/${deliveryId}`,
        { params: { admin_user_id: userId } }
      );
      
      setSelectedDelivery(response.data);
      setShowModal(true);
      setIsOpen(false);
      setSearchQuery('');
      setSearchResults([]);
    } catch (error) {
      console.error('Error fetching delivery details:', error);
    }
  }

  function handleClearSearch() {
    setSearchQuery('');
    setSearchResults([]);
  }

  return (
    <>
      <div className="relative" ref={searchRef}>
        {/* Search Button */}
        {!isOpen ? (
          <button
            onClick={() => setIsOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-slate-300 hover:border-[#B1336E] text-slate-700 font-semibold rounded-lg transition-all duration-200 font-montserrat"
          >
            <Search className="w-4 h-4" />
            <span className="hidden sm:inline">Buscar Entrega</span>
          </button>
        ) : (
          <div className="w-64 sm:w-80">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="ID, nombre o teléfono..."
                autoFocus
                className="w-full pl-10 pr-10 py-2 border-2 border-[#B1336E] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 font-montserrat text-sm"
              />
              {searchQuery && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded transition-colors"
                >
                  <X className="w-4 h-4 text-slate-400" />
                </button>
              )}
            </div>

            {/* Dropdown Results */}
            {(loading || searchResults.length > 0 || (searchQuery.length >= 2 && searchResults.length === 0)) && (
              <div className="absolute z-50 w-full mt-2 bg-white border border-slate-200 rounded-lg shadow-xl max-h-96 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center p-6">
                    <Loader2 className="w-6 h-6 text-[#B1336E] animate-spin" />
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="p-6 text-center">
                    <p className="text-sm text-slate-500 font-montserrat">
                      No se encontraron resultados
                    </p>
                  </div>
                ) : (
                  <div className="py-2">
                    {searchResults.map((delivery) => (
                      <button
                        key={delivery.id}
                        onClick={() => handleSelectDelivery(delivery.id)}
                        className="w-full px-4 py-3 hover:bg-slate-50 transition-colors text-left border-b border-slate-100 last:border-0"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-bold text-[#B1336E] font-montserrat">
                            ID: {delivery.id}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold font-montserrat ${
                            delivery.status === 'entregado'
                              ? 'bg-[#67A43C]/10 text-[#67A43C]'
                              : delivery.status === 'rechazado'
                              ? 'bg-red-50 text-red-600'
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {delivery.status}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-slate-900 font-montserrat truncate">
                          {delivery.recipient_name}
                        </p>
                        <p className="text-xs text-slate-600 font-montserrat truncate">
                          {delivery.recipient_phone}
                        </p>
                        {delivery.address && (
                          <p className="text-xs text-slate-500 font-montserrat truncate mt-1">
                            {delivery.address}
                          </p>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delivery Modal */}
      {showModal && selectedDelivery && (
        <DeliverySearchModal
          delivery={selectedDelivery}
          onClose={() => {
            setShowModal(false);
            setSelectedDelivery(null);
          }}
        />
      )}
    </>
  );
}

export default DeliverySearchButton;